import './form.scss';

import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon, { ICON_TYPE } from '../icon';

import Grid from './grid';
import Anchor from './anchor';

/**
 * <Form />
 */
const Form = forwardRef((props, ref) => {
  const className = classNames({
    form: true,
    [props.className]: props.className,
  });

  return (
    <form
      ref={ref}
      method={props.method}
      action={props.action}
      className={className}
      onSubmit={props.onSubmit}
      target={props.targetBlank ? '_blank' : undefined}
    >
      {props.children}
    </form>
  );
});

Form.propTypes = {
  method: PropTypes.oneOf(['GET', 'POST']),
  action: PropTypes.string,
  targetBlank: PropTypes.bool,
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

/**
 * <Form.Group />
 */
const Group = forwardRef((props, ref) => {
  const className = classNames({
    'form-group': true,
    [props.className]: props.className,
  });

  return (
    <div ref={ref} className={className}>
      {props.children}
    </div>
  );
});

Group.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

/**
 * <Form.Label />
 */
const Label = forwardRef((props, ref) => {
  const className = classNames({
    label: true,
    'sr-only': props.srOnly,
    [props.className]: props.className,
  });

  return (
    <label ref={ref} htmlFor={props.htmlFor} className={className}>
      {props.children}
    </label>
  );
});

Label.propTypes = {
  htmlFor: PropTypes.string,
  srOnly: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

Label.defaultProps = {
  srOnly: false,
};

/**
 * <Form.Input />
 */
const Input = forwardRef((props, ref) => {
  const [type, setType] = useState(props.type);

  if (props.type === 'textarea') {
    return <Textarea ref={ref} {...props} />;
  }

  if (props.type === 'radio' || props.type === 'checkbox') {
    const className = classNames({
      'form-check-input': true,
      [`form-control-${props.size}`]: props.size,
      [props.className]: props.className,
    });

    const id = props.id || `${props.name}-${props.value}`;

    return (
      <div className="form-check">
        <input
          ref={ref}
          id={id}
          type={props.type}
          name={props.name}
          value={props.value}
          className={className}
          onChange={props.onChange}
          readOnly={props.disabled}
          checked={props.checked}
        />
        {props.placeholder && (
          <label className="label form-check-label" htmlFor={id}>
            {props.placeholder}
          </label>
        )}
      </div>
    );
  }

  const className = classNames({
    'form-control': true,
    [`form-control-${props.size}`]: props.size,
    'is-valid': props.isValid,
    'is-invalid': props.isInvalid,
    [props.className]: props.className,
  });

  return (
    <div className="position-relative">
      <input
        ref={ref}
        id={props.id}
        type={type}
        name={props.name}
        value={props.value}
        placeholder={props.placeholder}
        className={className}
        onChange={props.onChange}
        readOnly={props.disabled}
        required={props.required}
      />
      {props.type === 'password' && (
        <div className="position-absolute h-100 d-flex align-items-center" style={{ right: "1rem", top: 0 }}>
          <Anchor onClick={(e) => { e.preventDefault(); setType(val => val === "password" ? "text" : "password") }}>
            <Icon type={type === "password" ? ICON_TYPE.EYE : ICON_TYPE.EYE_X} />
          </Anchor>
        </div>
      )}
    </div>
  );
});

Input.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'lg']),
  name: PropTypes.string,
  value: PropTypes.string || PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  checked: PropTypes.bool,
  isValid: PropTypes.bool,
  isInvalid: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

Input.defaultProps = {
  type: 'text',
  disabled: false,
  required: false,
  checked: false,
  isValid: undefined,
  isInvalid: undefined,
};

/**
 * <Form.Textarea />
 */
const Textarea = forwardRef((props, ref) => {
  const className = classNames({
    'form-control': true,
    [props.className]: props.className,
  });

  return (
    <textarea
      ref={ref}
      id={props.id}
      name={props.name}
      rows={props.rows}
      placeholder={props.placeholder}
      className={className}
      onChange={props.onChange}
      disabled={props.disabled}
      defaultValue={props.defaultValue}
      style={{ resize: 'none' }}
    >
      {props.value}
    </textarea>
  );
});

Textarea.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  rows: PropTypes.number,
  value: PropTypes.string || PropTypes.number,
  defaultValue: PropTypes.string || PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

Textarea.defaultProps = {
  rows: 3,
  disabled: false,
};

/**
 * <Form.Select />
 */
const Select = forwardRef((props, ref) => {
  const className = classNames({
    'form-control': true,
    [`form-control-${props.size}`]: props.size,
    [props.className]: props.className,
  });

  return (
    <select
      ref={ref}
      value={props.value}
      defaultValue={props.defaultValue}
      onChange={props.onChange}
      className={className}
    >
      {props.children}
    </select>
  );
});

Select.propTypes = {
  value: PropTypes.any,
  defaultValue: PropTypes.any,
  size: PropTypes.oneOf(['sm', 'lg']),
  onChange: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

/**
 * <Form.SelectOption />
 */
const SelectOption = forwardRef((props, ref) => (
  <option ref={ref} value={props.value}>
    {props.children}
  </option>
));

SelectOption.propTypes = {
  value: PropTypes.any,
  children: PropTypes.node,
};

/**
 * <Form.Row />
 */
const Row = forwardRef((props, ref) => {
  const className = classNames({
    'form-row': true,
    'no-gutters': props.noGutters,
    [props.className]: props.className,
  });

  return (
    <div ref={ref} className={className}>
      {props.children}
    </div>
  );
});

Row.propTypes = {
  noGutters: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

Row.defaultProps = {
  noGutters: false,
};

/**
 * <Form.Col />
 */
const Col = Grid.Col;

Form.Group = Group;
Form.Label = Label;
Form.Input = Input;
Form.Textarea = Textarea;
Form.Select = Select;
Form.SelectOption = SelectOption;
Form.Row = Row;
Form.Col = Col;

export default Form;
